import React from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { toggleSidebar, toggleDarkMode } from '../../features/ui/uiSlice';
import { logout } from '../../features/auth/authSlice';
import { fetchUnreadCount } from '../../features/notifications/notificationSlice';
import { isUser, isCompany, isAdmin } from '../../features/auth/authSlice';
import {
    Bars3Icon,
    SunIcon,
    MoonIcon,
    BellIcon,
    ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const Header: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { darkMode } = useAppSelector((state) => state.ui);
    const { user } = useAppSelector((state) => state.auth);
    const { unreadCount, isLoading } = useAppSelector((state) => state.notifications);

    const handleLogout = async () => {
        try {
            await dispatch(logout()).unwrap();
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handleNotificationClick = () => {
        if (!user) return 'U';
        if (isUser(user)) {
            navigate('/dashboard/notifications');
        } else {
            navigate('/notifications');
        }
    };

    React.useEffect(() => {
        if (user && !isLoading) {
            dispatch(fetchUnreadCount());
        }
    }, [dispatch, user, isLoading]);

    const getUserDisplayName = () => {
        if (!user) return 'User';
        if (isUser(user)) return `${user.firstName} ${user.lastName}`;
        if (isCompany(user)) return user.companyName;
        if (isAdmin(user)) return `${user.firstName} ${user.lastName}`;
        return 'User';
    };

    const getUserInitial = () => {
        if (!user) return 'U';
        if (isUser(user)) return user.firstName?.charAt(0) || 'U';
        if (isCompany(user)) return user.companyName?.charAt(0) || 'C';
        if (isAdmin(user)) return user.firstName?.charAt(0) || 'A';
        return 'U';
    };

    const getUserRole = () => {
        if (!user) return 'User';
        if (isUser(user)) return user.designation || 'User';
        if (isCompany(user)) return 'Company';
        if (isAdmin(user)) return 'Administrator';
        return 'User';
    };

    return (
        <header className="sticky top-0 z-1 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-lg border-b border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center justify-between h-16 sm:h-18 lg:h-18 px-3 sm:px-4 lg:px-6 max-w-full">
                <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 flex-1 min-w-0">
                    <button
                        onClick={() => dispatch(toggleSidebar())}
                        className="p-2 sm:p-2.5 lg:p-3 rounded-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 lg:hidden flex-shrink-0 active:scale-95 touch-manipulation"
                        aria-label="Toggle sidebar"
                    >
                        <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>

                    <div className="flex-1 min-w-0 ml-1 sm:ml-2 lg:ml-0">
                        <h1 className="text-base sm:text-lg lg:text-lg xl:text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent truncate leading-tight">
                            {user && isAdmin(user) ? 'Admin Dashboard' :
                                user && isCompany(user) ? 'Company Dashboard' :
                                    'Dashboard'}
                        </h1>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-medium mt-0.5 hidden sm:block truncate">
                            {user && isAdmin(user) ? 'System Administrator' :
                                user && isCompany(user) ? 'Manage your business operations' :
                                    'Your professional workspace'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3 flex-shrink-0">
                    {/* Dark mode toggle */}
                    <button
                        onClick={() => dispatch(toggleDarkMode())}
                        className="p-2 sm:p-2.5 lg:p-3 rounded-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 active:scale-95 touch-manipulation"
                        title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                        aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                    >
                        {darkMode ? (
                            <SunIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                        ) : (
                            <MoonIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                        )}
                    </button>

                    {/* Notifications */}
                    <button
                        onClick={handleNotificationClick}
                        className="relative p-2 sm:p-2.5 lg:p-3 rounded-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 active:scale-95 touch-manipulation"
                        title="View notifications"
                        aria-label="Notifications"
                    >
                        <BellIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-0.5 sm:-top-1 -right-0.5 sm:-right-1 min-w-[20px] h-5 px-1.5 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-lg animate-pulse">
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>

                    {/* Profile section */}
                    <div className="flex items-center gap-2 sm:gap-2.5 lg:gap-3 p-1.5 sm:p-2 rounded-xl sm:rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-800/30 shadow-sm">
                        <div className="h-8 w-8 sm:h-9 sm:w-9 lg:h-10 lg:w-10 rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 flex items-center justify-center shadow-md ring-2 ring-white/50 dark:ring-gray-700/50 flex-shrink-0">
                            <span className="text-xs sm:text-sm font-bold text-white">
                                {getUserInitial()}
                            </span>
                        </div>
                        <div className="hidden lg:block text-left pr-1 min-w-0">
                            <p className="text-sm font-bold text-gray-900 dark:text-white truncate max-w-[120px] xl:max-w-[150px]">
                                {getUserDisplayName()}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium truncate max-w-[120px] xl:max-w-[150px]">
                                {user && isAdmin(user) ? 'Administrator' :
                                    user && isCompany(user) ? 'Company' :
                                        user && isUser(user) ? user.designation || 'User' : 'User'}
                            </p>
                        </div>
                    </div>

                    {/* Logout button */}
                    <button
                        onClick={handleLogout}
                        className="p-2 sm:p-2.5 lg:p-3 rounded-xl text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 hover:scale-105 active:scale-95 touch-manipulation"
                        title="Logout"
                        aria-label="Logout"
                    >
                        <ArrowRightOnRectangleIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                </div>
            </div>
        </header>
    );
};

export default Header;